import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from "recharts";
import "./AccuracyComparison.css";

const AccuracyComparison = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await import("../data/historical_comparison_summary.json");
        setData(response.default || response);
      } catch (error) {
        console.error("Failed to load comparison data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return <div className="accuracy-loading">Loading comparison data...</div>;
  }

  if (!data || !data.yearly_aggregate) {
    return <div className="accuracy-error">Comparison data not available</div>;
  }

  // Calculate overall stats
  const progsWithData = Object.values(data.prognosticator_stats).filter(p => p.total_years >= 5);
  const avgProgAccuracy = progsWithData.reduce((sum, p) => sum + p.prognosticator_accuracy, 0) / progsWithData.length;
  const avgBBAccuracy = progsWithData.reduce((sum, p) => sum + p.byteburrower_accuracy, 0) / progsWithData.length;
  const bbWins = progsWithData.filter(p => p.byteburrower_accuracy > p.prognosticator_accuracy).length;

  const summaryData = [
    { name: "Byte Burrower", accuracy: Math.round(avgBBAccuracy), fill: "#4CAF50" },
    { name: "Prognosticators", accuracy: Math.round(avgProgAccuracy), fill: "#3498DB" }
  ];

  // Filter to years with enough predictions (5+) for stable percentages
  const yearlyData = data.yearly_aggregate.filter(y => y.total_predictions >= 5);

  return (
    <div className="accuracy-comparison">
      <div className="accuracy-summary">
        <div className="stat-card bb-card">
          <div className="stat-value">{Math.round(avgBBAccuracy)}%</div>
          <div className="stat-label">Byte Burrower Accuracy</div>
        </div>
        <div className="stat-card prog-card">
          <div className="stat-value">{Math.round(avgProgAccuracy)}%</div>
          <div className="stat-label">Prognosticator Accuracy</div>
        </div>
        <div className="stat-card wins-card">
          <div className="stat-value">{bbWins}/{progsWithData.length}</div>
          <div className="stat-label">Byte Burrower Head-to-Head Wins</div>
        </div>
      </div>

      <div className="chart-section">
        <h3>Overall Accuracy Comparison</h3>
        <p className="chart-subtitle">Average accuracy across {progsWithData.length} prognosticators with 5+ years of predictions</p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={summaryData} layout="vertical" margin={{ left: 20, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <YAxis type="category" dataKey="name" width={120} />
            <Tooltip formatter={(value) => [`${value}%`, "Accuracy"]} />
            <Bar dataKey="accuracy" radius={[0, 4, 4, 0]}>
              {summaryData.map((entry, index) => (
                <rect key={index} fill={entry.fill} />
              ))}
            </Bar>
            <ReferenceLine x={50} stroke="#666" strokeDasharray="5 5" label={{ value: "50%", position: "top" }} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-section">
        <h3>Accuracy Over Time</h3>
        <p className="chart-subtitle">Year-by-year accuracy rates across all prognosticators</p>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={yearlyData} margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="year"
              tickFormatter={(v) => v % 10 === 0 ? v : ""}
              interval={0}
            />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              formatter={(value, name) => [
                `${value}%`,
                name === "byteburrower_accuracy" ? "Byte Burrower" : "Prognosticators"
              ]}
              labelFormatter={(label) => `Year: ${label}`}
            />
            <Legend
              formatter={(value) =>
                value === "byteburrower_accuracy" ? "Byte Burrower" : "Prognosticators"
              }
            />
            <ReferenceLine y={50} stroke="#999" strokeDasharray="5 5" />
            <Line
              type="monotone"
              dataKey="byteburrower_accuracy"
              stroke="#4CAF50"
              strokeWidth={2}
              dot={false}
              name="byteburrower_accuracy"
            />
            <Line
              type="monotone"
              dataKey="prognosticator_accuracy"
              stroke="#3498DB"
              strokeWidth={2}
              dot={false}
              name="prognosticator_accuracy"
            />
          </LineChart>
        </ResponsiveContainer>
        <p className="chart-note">Note: Years with fewer than 5 predictions are excluded to avoid unstable percentages from small samples.</p>
      </div>

      <p className="methodology-note">
        <strong>Methodology:</strong> Byte Burrower's historical predictions are calculated retroactively using the same algorithm applied to past weather data. For each year, the prediction is based on the proportion of "early spring" years in that location's history. A year is classified as "early spring" if February or March temperatures exceeded the 15-year rolling average.
      </p>
    </div>
  );
};

export default AccuracyComparison;
